import React, { useEffect, useState } from "react";
import AccountsIcon from "../../icons/accounts-icon";
import Text from "../../common/typography/text";
import ActionsMenuComponent from "../../common/actions-menu-component";
import NotesIcon from "../../icons/notes-icon";
import { formattedDate } from "../Utils/Helpers";
import ModalView from "../../common/modal";
import Button from "../../common/button/button";
import { Checkbox } from "antd";
import Swal from "sweetalert2";
import APIService from "../../Service/APIService";

const NotesSection = (props) => {
  const { invoiceData, invoiceNotesAccount, invoiceNotesDistributor, userData } = props;

  const [notesByType, setNotesByType] = useState({
    account: invoiceNotesAccount,
    distributor: invoiceNotesDistributor,
  });
  const [isNotifyCheck, setIsNotifyCheck] = useState(false);
  const [isEditModal, setIsEditModal] = useState(false);
  const [activeNote, setActiveNote] = useState(null);
  const [editNoteText, setEditNoteText] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    setNotesByType({
      account: invoiceNotesAccount,
      distributor: invoiceNotesDistributor,
    });
  }, [invoiceNotesAccount, invoiceNotesDistributor]);

  const accountNote = notesByType.account;
  const distributorNote = notesByType.distributor;

  const formattedAccountDate = formattedDate(accountNote?.NoteCreatedAt);
  const formattedDistributorDate = formattedDate(
    distributorNote?.NoteCreatedAt
  );

  const handleEditOpen = (note, noteType) => {
    if (!note) return;
    setActiveNote({
      ...note,
      NoteType: note?.NoteType ?? noteType,
    });
    setEditNoteText(note?.Note ?? "");
    setIsNotifyCheck(Boolean(note?.NotifyByEmail));
    setIsEditModal(true);
  };

  const handleEditClose = () => {
    setIsEditModal(false);
    setActiveNote(null);
    setEditNoteText("");
    setIsNotifyCheck(false);
    setIsSaving(false);
  };

  const handleDeleteNote = (note, noteType) => {
    if (!note || isDeleting) return;
    const noteId = note?.ID ?? note?.NoteID;
    if (!noteId) {
      Swal.fire({
        icon: "error",
        title: "Missing note",
        text: "Note data is not available. Please try again.",
      });
      return;
    }
    Swal.fire({
      title: "Are you sure?",
      text: "The note will get deleted",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (!result.isConfirmed) return;
      setIsDeleting(true);
      try {
        const apiServe = new APIService();
        const response = await apiServe.addDataToBackend(
          {},
          `DeleteInvoiceNote?InvoiceNoteID=${noteId}`
        );
        if (response?.status === 200 || response?.status === 201) {
          const resolvedType = note?.NoteType ?? noteType;
          setNotesByType((prev) => ({
            ...prev,
            account:
              resolvedType === "Account" ? null : prev.account,
            distributor:
              resolvedType === "Distributor" ? null : prev.distributor,
          }));
          if ((activeNote?.ID ?? activeNote?.NoteID) === noteId) {
            handleEditClose();
          }
          Swal.fire("Deleted!", "Note deleted successfully", "success");
        } else {
          Swal.fire({
            icon: "error",
            title: "Note delete failed",
            text: "Please try again.",
          });
        }
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "Note delete failed",
          text: "Please try again.",
        });
      } finally {
        setIsDeleting(false);
      }
    });
  };

  const editInvoiceNote = async () => {
    if (!activeNote || isSaving) {
      return;
    }
    if (!editNoteText?.trim()) {
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: "Please enter note content",
      });
      return;
    }
    const invoiceId = invoiceData?.ID ?? invoiceData?.InvoiceID;
    if (!invoiceId) {
      Swal.fire({
        icon: "error",
        title: "Missing invoice",
        text: "Invoice data is not available. Please try again.",
      });
      return;
    }
    const noteId = activeNote?.ID ?? activeNote?.NoteID;
    if (!noteId) {
      Swal.fire({
        icon: "error",
        title: "Missing note",
        text: "Note data is not available. Please try again.",
      });
      return;
    }

    const isAccountNote = activeNote?.NoteType === "Account";
    const isDistributorNote = activeNote?.NoteType === "Distributor";
    const requestPayload = {
      InvoiceID: invoiceId,
      NoteID: noteId,
      NoteType: activeNote?.NoteType,
      NoteContent: editNoteText,
      NotifyByEmail: isNotifyCheck,
      AccountID: isAccountNote
        ? activeNote?.AccountID ?? invoiceData?.AccountID ?? null
        : null,
      DistributorID: isDistributorNote
        ? activeNote?.DistributorID ?? invoiceData?.DistributorID ?? null
        : null,
    };

    setIsSaving(true);
    try {
      const apiServe = new APIService();
      const response = await apiServe.addDataToBackend(
        Object.fromEntries(
          Object.entries(requestPayload).filter(
            ([, value]) => value !== undefined
          )
        ),
        "UpdateInvoiceNote"
      );
      if (response?.status === 200 || response?.status === 201) {
        setNotesByType((prev) => {
          if (activeNote?.NoteType === "Account") {
            return {
              ...prev,
              account: {
                ...prev.account,
                Note: editNoteText,
              },
            };
          }
          if (activeNote?.NoteType === "Distributor") {
            return {
              ...prev,
              distributor: {
                ...prev.distributor,
                Note: editNoteText,
              },
            };
          }
          return prev;
        });
        Swal.fire("Updated!", "Note updated successfully", "success").then(
          handleEditClose
        );
      } else {
        Swal.fire({
          icon: "error",
          title: "Note update failed",
          text: "Please try again",
        });
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Note update failed",
        text: "Please try again",
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <div className="column-layout-division-comments-container">
        {accountNote && accountNote?.Note && (userData?.Type === "Account" || userData?.Type === "Admin" || userData?.Type === "Viewer") ? (
          <div className="invoice-page-note-container">
            <div className="invoice-page-note-header">
              <div className="invoice-page-note-header-left">
                <AccountsIcon />
                <Text variant="smallText">Account Note</Text>
              </div>
              <div className="invoice-page-note-header-right">
                <div className="invoice-page-header-right-name-container">
                  {accountNote?.NoteByUserName && (
                    <>
                      <Text variant="smallText">
                        {accountNote?.NoteByUserName}
                      </Text>
                      <div className="invoice-page-ellipse" />
                    </>
                  )}
                  <Text variant="smallText" color="lightText">
                    {formattedAccountDate}
                  </Text>
                </div>
                {userData && (userData.Type === "Admin") &&
                  <ActionsMenuComponent
                    onEdit={() => handleEditOpen(accountNote, "Account")}
                    handleDeleteComment={() =>
                      handleDeleteNote(accountNote, "Account")
                    }
                  />
                }
              </div>
            </div>
            <div className="invoice-page-note-body">
              <NotesIcon />
              <Text variant="smallText">{accountNote?.Note}</Text>
            </div>
          </div>
        ) : null}

        {distributorNote && distributorNote?.Note && (userData?.Type === "Distributor" || userData?.Type === "Admin" || userData?.Type === "Viewer") ? (
          <div className="invoice-page-note-container">
            <div className="invoice-page-note-header">
              <div className="invoice-page-note-header-left">
                <AccountsIcon />
                <Text variant="smallText">Distributor Note</Text>
              </div>
              <div className="invoice-page-note-header-right">
                <div className="invoice-page-header-right-name-container">
                  {distributorNote?.NoteByUserName && (
                    <>
                      <Text variant="smallText">
                        {distributorNote?.NoteByUserName}
                      </Text>
                      <div className="invoice-page-ellipse" />
                    </>
                  )}
                  <Text variant="smallText" color="lightText">
                    {formattedDistributorDate}
                  </Text>
                </div>
                {userData && (userData.Type === "Admin") &&
                  <ActionsMenuComponent
                    onEdit={() =>
                      handleEditOpen(distributorNote, "Distributor")
                    }
                    handleDeleteComment={() =>
                      handleDeleteNote(distributorNote, "Distributor")
                    }
                  />
                }
              </div>
            </div>
            <div className="invoice-page-note-body">
              <NotesIcon />
              <Text variant="smallText">{distributorNote?.Note}</Text>
            </div>
          </div>
        ) : null}
      </div>
      {isEditModal && (
        <ModalView
          isOpen={isEditModal}
          onClose={handleEditClose}
          modalTitle="Edit note"
          textInputComponent={
            <div className="field-group-wrapper">
              <div className="add-note-model-input-container">
                <textarea
                  className="add-note-textarea"
                  placeholder="Add a note"
                  maxLength={1000}
                  value={editNoteText}
                  onChange={(event) => setEditNoteText(event.target.value)}
                />
                <div className="comments-individual-header-left">
                  <Checkbox
                    checked={isNotifyCheck}
                    onClick={() => setIsNotifyCheck(!isNotifyCheck)}
                  />
                  <Text variant="smallText">Notify To via email</Text>
                </div>
              </div>
            </div>
          }
          footerButtonWrapper={
            <div
              className="footer-button-wrapper-flex-end"
              style={{ gap: "12px" }}
            >
              <Button
                variant="table"
                subVariant="outline"
                onClick={handleEditClose}
                disabled={isSaving}
              >
                Cancel
              </Button>
              <Button
                variant="table"
                subVariant="primary"
                onClick={editInvoiceNote}
                disabled={isSaving || !editNoteText?.trim()}
              >
                Save
              </Button>
            </div>
          }
        />
      )}
    </div>
  );
};

export default NotesSection;
